import { useCallback, useEffect, useRef, useState } from 'react'
import { InventoryItem } from 'types/model'

import { useCheckoutContext } from '@hooks/checkout'
import { LoadingIcon } from '@icons/loading'
import { useQueryParams } from '@hooks/query-params'
import { Button } from '@elements/button'
import { ErrorNote } from '@elements/error-note'

export function BookingProxy({ inventory }: { inventory: InventoryItem }) {
  const [bookingError, setBookingError] = useState<string>()
  const inFlightRef = useRef(false)
  const hasStartedRef = useRef(false)
  const hasSucceededRef = useRef(false)
  const isUnmountedRef = useRef(false)
  const attemptSessionRef = useRef(0)
  const { beginCheckout } = useCheckoutContext()
  const queryParams = useQueryParams()

  const buildTerminalErrorMessage = useCallback(
    (err: unknown) => {
      const rawMessage =
        err instanceof Error && err.message
          ? err.message
          : 'Unable to start booking. Please try again.'
      return `Unable to start booking for inventory ${inventory.id}: ${rawMessage}`
    },
    [inventory.id]
  )

  const startBookingAttempt = useCallback(
    async (sessionId: number) => {
      if (isUnmountedRef.current) return
      if (attemptSessionRef.current !== sessionId) return
      if (hasSucceededRef.current) return
      if (inFlightRef.current) return

      const quantity = Number(queryParams.quantity ?? 1)
      const arrivalTime = queryParams.arrivalTime
        ? Number(queryParams.arrivalTime)
        : null

      inFlightRef.current = true

      try {
        await beginCheckout({
          inventoryId: inventory.id,
          quantity,
          arrivalTime,
          client: 'web-fast-pass',
        })
        hasSucceededRef.current = true
      } catch (err) {
        console.error('Fast Pass booking failed:', err)

        if (!isUnmountedRef.current) {
          setBookingError(buildTerminalErrorMessage(err))
        }
      } finally {
        inFlightRef.current = false
      }
    },
    [
      beginCheckout,
      buildTerminalErrorMessage,
      inventory.id,
      queryParams.arrivalTime,
      queryParams.quantity,
    ]
  )

  const startBookingAttemptRef = useRef(startBookingAttempt)
  startBookingAttemptRef.current = startBookingAttempt

  useEffect(() => {
    if (hasStartedRef.current) return
    hasStartedRef.current = true
    isUnmountedRef.current = false
    hasSucceededRef.current = false

    setBookingError(undefined)
    inFlightRef.current = false

    const sessionId = attemptSessionRef.current + 1
    attemptSessionRef.current = sessionId
    void startBookingAttemptRef.current(sessionId)

    return () => {
      isUnmountedRef.current = true
      attemptSessionRef.current += 1
    }
  }, [])

  const onRetry = useCallback(() => {
    if (inFlightRef.current) return

    hasSucceededRef.current = false
    setBookingError(undefined)
    inFlightRef.current = false

    const sessionId = attemptSessionRef.current + 1
    attemptSessionRef.current = sessionId
    void startBookingAttempt(sessionId)
  }, [startBookingAttempt])

  return (
    <div className="flex flex-col items-center justify-center w-full mx-auto h-[100vh]">
      {bookingError ? (
        <div className="max-w-lg px-6 mx-auto">
          <ErrorNote>{bookingError}</ErrorNote>
          <Button.Secondary className="w-full mt-4" onClick={onRetry}>
            Try again
          </Button.Secondary>
        </div>
      ) : (
        <LoadingIcon className="w-[28px] h-[28px] m-auto text-primary-400" />
      )}
    </div>
  )
}
